import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from '@/utils/authContext';
import logger from '@/utils/logger';

interface CartItem {
  id: string;
  name: string;
  location: string;
  state: string;
  price: number;
  impressions: number;
  size: string;
  type: string;
  status: "available" | "sold-out";
  image: string;
  spots?: number;
  campaignDuration?: number;
  dailyHours?: number;
  isDeal?: boolean;
  daypartSchedules?: string[];
  packageStartDate?: string;
  packageEndDate?: string;
  requiresCampaignDuration?: boolean;
  countryId?: string;
  countryName?: string;
  currencyCode?: string;
  // New API fields for package pricing
  totalPrice?: number;
  validityStartDate?: string;
  validityEndDate?: string;
  specifications: {
    resolution: string;
    duration: string;
  };
}

interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: number | string) => void;
  clearCart: () => void;
  isInCart: (id: number | string) => boolean;
  cartCount: number;
  setCartItemCount: (count: number) => void;
  totalCost: number;
  totalImpressions: number;
  updateSpots: (id: string, spots: number) => void;
  updateCampaignSettings: (id: string, duration: number, hours: number) => void;
  updateCampaignDuration: (id: string, duration: number) => void;
  updateDaypartSchedules: (id: string, schedules: string[]) => void;
  requiresCampaignDates: () => boolean;
  getAutomaticCampaignDates: () => { startDate: string; endDate: string } | null;
  hasInventoryTypeMix: () => boolean;
  getInventoryTypeBreakdown: () => { digital: number; classic: number; packages: number; deals: number; other: number };
  setCartItems: (items: CartItem[]) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [cartCount, setCartItemCount] = useState(0);
  const { isLoggedIn } = useAuth();

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('billboardCart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);
        const cartVersion = localStorage.getItem('cartVersion');
        if (cartVersion && cartVersion !== '2.0') {
          // Only clear if there's an existing version that's different
          localStorage.setItem('cartVersion', '2.0');
          localStorage.removeItem('billboardCart');
          setCartItems([]);
        } else {
          // If no version exists or version is correct, load the cart and set version
          if (!cartVersion) {
            localStorage.setItem('cartVersion', '2.0');
          }
          setCartItems(cartData);
          setCartItemCount(cartData.length);
        }
      } catch (error) {
        logger.error('Error loading cart from localStorage:', error);
      }
    } else {
      // Set version even if no cart exists
      const cartVersion = localStorage.getItem('cartVersion');
      if (!cartVersion) {
        localStorage.setItem('cartVersion', '2.0');
      }
    }
  }, []);

  // Save cart to localStorage and update cartCount whenever cartItems changes
  useEffect(() => {
    localStorage.setItem('billboardCart', JSON.stringify(cartItems));
    setCartItemCount(cartItems.length);
    window.dispatchEvent(new Event('cartUpdated'));
  }, [cartItems]);

  // Clear cart when user logs out
  useEffect(() => {
    if (!isLoggedIn) {
      // Keep cart for non-logged-in users
    }
  }, [isLoggedIn]);

  // Calculate totalCost
  const totalCost = cartItems.reduce((sum, item) => {
    if (item.isDeal || item.type === "Deal") {
      return sum + (item.price / 100);
    }
    if (item.type === "Digital" && item.spots && item.campaignDuration) {
      const spotPrice = item.price / 100;
      const dailyCost = spotPrice * item.spots;
      const totalCost = dailyCost * item.campaignDuration;
      return sum + totalCost;
    }
    if (item.type === "Classic" && item.campaignDuration) {
      const monthlyRate = item.price / 100;
      const dailyRate = monthlyRate / 30;
      const totalCost = dailyRate * item.campaignDuration;
      return sum + totalCost;
    }
    if (item.type === "Package") {
      return sum + (item.price / 100);
    }
    return sum + (item.price / 100);
  }, 0);

  // Calculate totalImpressions
  const totalImpressions = cartItems.reduce((sum, item) => sum + item.impressions, 0);

  const updateSpots = (id: string, spots: number) => {
    setCartItems(prev => prev.map(item =>
      item.id === id ? { ...item, spots: Math.max(1, spots) } : item
    ));
  };

  const updateCampaignSettings = (id: string, duration: number, hours: number) => {
    setCartItems(prev => prev.map(item => {
      if (item.id === id) {
        const minDuration = item.type === "Classic" ? 30 : 1;
        return {
          ...item,
          campaignDuration: Math.max(minDuration, duration),
          dailyHours: Math.max(15, Math.min(18, hours))
        };
      }
      return item;
    }));
  };

  const updateCampaignDuration = (id: string, duration: number) => {
    setCartItems(prev => prev.map(item => {
      if (item.id === id) {
        const minDuration = item.type === "Classic" ? 30 : 1;
        return {
          ...item,
          campaignDuration: Math.max(minDuration, duration)
        };
      }
      return item;
    }));
  };

  const updateDaypartSchedules = (id: string, schedules: string[]) => {
    setCartItems(prev => prev.map(item => {
      if (item.id === id && item.type === "Digital") {
        return {
          ...item,
          daypartSchedules: schedules
        };
      }
      return item;
    }));
  };

  const getInventoryTypeProperties = (type: string) => {
    switch (type) {
      case "Digital":
      case "Classic":
        return { requiresCampaignDuration: true };
      case "Package":
      case "Deal":
        return {
          requiresCampaignDuration: false,
          packageStartDate: "2025-09-01",
          packageEndDate: "2025-11-30"
        };
      default:
        return { requiresCampaignDuration: false };
    }
  };

  const requiresCampaignDates = () => {
    return cartItems.some(item => {
      const props = getInventoryTypeProperties(item.type);
      return props.requiresCampaignDuration;
    });
  };

  const getAutomaticCampaignDates = () => {
    const packagesAndDeals = cartItems.filter(item =>
      item.type === "Package" || item.type === "Deal"
    );

    if (packagesAndDeals.length === 0) return null;

    let latestStartDate = "";
    let latestEndDate = "";

    packagesAndDeals.forEach(item => {
      const props = getInventoryTypeProperties(item.type);
      if (props.packageStartDate && (!latestStartDate || props.packageStartDate > latestStartDate)) {
        latestStartDate = props.packageStartDate;
      }
      if (props.packageEndDate && (!latestEndDate || props.packageEndDate > latestEndDate)) {
        latestEndDate = props.packageEndDate;
      }
    });

    return latestStartDate && latestEndDate ?
      { startDate: latestStartDate, endDate: latestEndDate } : null;
  };

  const hasInventoryTypeMix = () => {
    const breakdown = getInventoryTypeBreakdown();
    const nonZeroTypes = Object.values(breakdown).filter(count => count > 0).length;
    return nonZeroTypes > 1;
  };

  const getInventoryTypeBreakdown = () => {
    return cartItems.reduce((breakdown, item) => {
      switch (item.type) {
        case "Digital":
          breakdown.digital++;
          break;
        case "Classic":
          breakdown.classic++;
          break;
        case "Package":
          breakdown.packages++;
          break;
        case "Deal":
          breakdown.deals++;
          break;
        default:
          breakdown.other++;
          break;
      }
      return breakdown;
    }, { digital: 0, classic: 0, packages: 0, deals: 0, other: 0 });
  };

  const addToCart = (item: CartItem) => {
    setCartItems(prev => {
      if (prev.find(cartItem => cartItem.id === item.id)) {
        return prev;
      }
      const typeProps = getInventoryTypeProperties(item.type);
      const itemWithDefaults = item.type === "Digital" ? {
        ...item,
        ...typeProps,
        spots: 1,
        campaignDuration: 30,
        dailyHours: 18,
        daypartSchedules: ["Morning", "Afternoon", "Evening"]
      } : item.type === "Classic" ? {
        ...item,
        ...typeProps,
        campaignDuration: 30
      } : {
        ...item,
        ...typeProps
      };
      return [...prev, itemWithDefaults];
    });
  };

  const removeFromCart = (id: number | string) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const isInCart = (id: number | string) => {
    return cartItems.some(item => item.id === id);
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      clearCart,
      isInCart,
      cartCount,
      setCartItemCount,
      totalCost,
      totalImpressions,
      updateSpots,
      updateCampaignSettings,
      updateCampaignDuration,
      updateDaypartSchedules,
      requiresCampaignDates,
      getAutomaticCampaignDates,
      hasInventoryTypeMix,
      getInventoryTypeBreakdown,
      setCartItems
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}